import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress/CircularProgress"
import { ChevronLeftOutlined, KeyboardArrowRightOutlined } from "@mui-symbols-material/w300"
import { Link, useNavigate } from "@tanstack/react-router"
import type { FC } from "react"
import { useState } from "react"

import ConfirmationDialog from "../../../shared/components/common/Modals/ConfirmationDialog"

import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import type { Product } from "@/graphql/codegen/graphql"
import { ProductVersionStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import { useDraftProductVersionMutation } from "@/screens/Products/hooks/useDraftProductVersionMutation"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface ProductHeaderActionsProps {
  product: Product
  hasValidShipFrom: boolean
  onStartPurchaseOrder: () => void
}

const ProductHeaderActions: FC<ProductHeaderActionsProps> = ({ product, hasValidShipFrom, onStartPurchaseOrder }) => {
  const navigate = useNavigate({ from: "/app/product/$productId/edit/information" })
  const { hasPermissions } = usePermissionManager()

  const hasMaintenancePermission = hasPermissions([PermissionActionType.PRODUCT_MAINTENANCE])
  const hasPurchaseOrderCreationPermission = hasPermissions([PermissionActionType.PURCHASE_ORDER_CREATION])
  const isDraftVersion = product.versionStatus === ProductVersionStatus.Draft
  const hasDraftVersion = product.versions?.some((el) => el.status === ProductVersionStatus.Draft)

  const canCreateDraft = hasMaintenancePermission && product.isActive
  const [isLoading, setIsLoading] = useState(false)

  const { createDraftProductVersion, deleteDraftProductVersion, publishDraftProductVersion } =
    useDraftProductVersionMutation()

  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [isAlreadyExistDialogOpen, setIsAlreadyExistDialogOpen] = useState<boolean>(false)
  const [isConfirmationDeleteDialogOpen, setIsConfirmationDeleteDialogOpen] = useState<boolean>(false)
  const [isConfirmationPublishDialogOpen, setIsConfirmationPublishDialogOpen] = useState<boolean>(false)

  const handleClickCreateNewVersion = async () => {
    if (hasDraftVersion) {
      setIsAlreadyExistDialogOpen(true)
    } else {
      setIsLoading(true)
      const { data, error } = await createDraftProductVersion({
        input: {
          productId: product.productId,
        },
      })

      const hasError =
        error?.message ||
        (data?.product.createDraftProductVersion.__typename === "ProductCreateDraftVersionFailure" &&
          Boolean(data?.product.createDraftProductVersion.error))

      if (hasError) {
        notify({
          type: NotificationType.Error,
          message: "Unable to create draft version. Please try again.",
        })
        setIsLoading(false)
        return
      }

      notify({
        type: NotificationType.Success,
        message: "Draft version created successfully",
      })

      setIsLoading(false)

      if (data?.product.createDraftProductVersion.__typename === "ProductCreateDraftVersionSuccess") {
        const productVersions = data?.product.createDraftProductVersion?.product?.versions

        await navigate({
          search: (prev) => ({
            ...prev,
            version: productVersions?.find((el) => el.status === ProductVersionStatus.Draft)?.versionNumber,
          }),
          replace: true,
        })
      }
    }
  }

  const handleDeleteDraft = async () => {
    setIsLoading(true)
    const { data, error } = await deleteDraftProductVersion({
      input: {
        productId: product.productId,
      },
    })

    const hasError =
      error?.message ||
      (data?.product.deleteDraftProductVersion.__typename === "ProductDeleteDraftVersionFailure" &&
        Boolean(data?.product.deleteDraftProductVersion.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message: "Unable to delete draft version. Please try again.",
      })
      setIsLoading(false)
      return
    }
    setIsConfirmationDeleteDialogOpen(false)
    setIsLoading(false)

    await navigate({
      search: (prev) => ({
        ...prev,
        version: undefined,
      }),
      replace: true,
    })

    notify({
      type: NotificationType.Success,
      message: "Draft version deleted successfully",
    })
  }

  const handleConfirmNavigateToDraft = async () => {
    const draftVersion = product.versions?.find((el) => el.status === ProductVersionStatus.Draft)?.versionNumber

    await navigate({
      search: (prev) => ({
        ...prev,
        version: draftVersion,
      }),
      replace: true,
    })

    setIsAlreadyExistDialogOpen(false)
  }

  const handlePublishProductVersion = async () => {
    setIsLoading(true)

    const { data, error } = await publishDraftProductVersion({ input: { productId: product.productId } })

    const hasError =
      error?.message ||
      (data?.product.publishDraftProductVersion.__typename === "ProductPublishDraftVersionFailure" &&
        Boolean(data?.product.publishDraftProductVersion.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message: "Unable to publish draft version. Please try again.",
      })
      setIsLoading(false)
      return
    }

    notify({
      type: NotificationType.Success,
      message: "Draft version published successfully",
    })

    setIsConfirmationPublishDialogOpen(false)
    setIsLoading(false)
    await navigate({
      search: (prev) => ({
        ...prev,
        version: undefined,
      }),
      replace: true,
    })
  }

  const DraftHeaderActions = (
    <Box className='flex justify-between gap-2'>
      <Button
        appearance='outlined'
        color='primary'
        onClick={() => setIsConfirmationDeleteDialogOpen(true)}
        disabled={!hasMaintenancePermission || isLoading}
        data-testid='product-delete-draft-btn'
      >
        Delete Draft
      </Button>

      <Button
        appearance='contained'
        color='primary'
        onClick={() => setIsConfirmationPublishDialogOpen(true)}
        data-testid='product-publish-new-version-btn'
        disabled={!hasMaintenancePermission || isLoading}
        endIcon={
          isLoading ? (
            <Box className='flex items-center'>
              <CircularProgress size={16} />
            </Box>
          ) : undefined
        }
      >
        Publish New Version
      </Button>
    </Box>
  )

  const HeaderActions = (
    <Box className='flex justify-between gap-2'>
      {canCreateDraft && (
        <Button
          appearance='outlined'
          color='primary'
          onClick={handleClickCreateNewVersion}
          data-testid='product-create-new-version-btn'
          disabled={isLoading}
          endIcon={
            isLoading ? (
              <Box className='flex items-center'>
                <CircularProgress size={16} />
              </Box>
            ) : undefined
          }
        >
          Create New Version
        </Button>
      )}
      {hasPurchaseOrderCreationPermission && (
        <Tooltip
          title='Add Ship From Location in order to create PO'
          disableHoverListener={hasValidShipFrom}
          disableFocusListener={hasValidShipFrom}
          disableTouchListener={hasValidShipFrom}
        >
          <Button
            appearance='outlined'
            color='primary'
            endIcon={<KeyboardArrowRightOutlined />}
            onClick={onStartPurchaseOrder}
            disabled={!hasValidShipFrom}
            data-testid='product-start-po-btn'
          >
            Start PO
          </Button>
        </Tooltip>
      )}
    </Box>
  )

  return (
    <Box className='flex justify-between'>
      <Box className='flex items-center gap-4'>
        <Link to='/app/companies/$companyId/edit/products' params={{ companyId: product?.company?.companyId }}>
          <IconButton aria-label='back to company' className='rounded bg-gray-200 text-primary'>
            <ChevronLeftOutlined />
          </IconButton>
        </Link>
        <Typography variant='h5' color='primary' fontWeight='normal'>
          Product Detail
        </Typography>
      </Box>
      {isDraftVersion ? DraftHeaderActions : HeaderActions}

      {isAlreadyExistDialogOpen && (
        <ConfirmationDialog
          title='Draft already exists for this product.'
          content='Please go back and edit current version or save draft as new version before creating another draft.'
          confirmLabel='Edit Current Draft'
          closeLabel='Back To Edit'
          onClose={() => setIsAlreadyExistDialogOpen(false)}
          onConfirm={handleConfirmNavigateToDraft}
        />
      )}
      {isConfirmationDeleteDialogOpen && (
        <ConfirmationDialog
          title='Oh no! Are you sure you want to discard your updates?'
          content='By proceeding you will lose any updates you’ve currently made. Click on “Go Back” if this action was a mistake.'
          confirmLabel='Proceed'
          closeLabel='Back To Editing'
          onClose={() => setIsConfirmationDeleteDialogOpen(false)}
          onConfirm={handleDeleteDraft}
        />
      )}

      {isConfirmationPublishDialogOpen && (
        <ConfirmationDialog
          title='Publish Draft Version?'
          content='Publishing this draft will make it the current version. The existing current version will become a past version.'
          confirmLabel='Publish Version'
          closeLabel='Cancel'
          onClose={() => setIsConfirmationPublishDialogOpen(false)}
          onConfirm={handlePublishProductVersion}
        />
      )}
    </Box>
  )
}

export default ProductHeaderActions
